import { useCallback } from 'react';
import { numberFormat } from '@src/common/service/numberFormatHelper';

/**
 * 給与表示ラベル生成カスタムフック
 * 
 * 給与フィルターの表示ラベルを生成するためのロジックを提供します。
 * 給与種別、最小値、最大値、通貨、単位などの情報から適切な表示文字列を生成します。
 * 
 * @returns {Object} 給与表示ラベル生成に必要な関数群
 */
const useSalaryDisplayLabel = () => {
    // 給与種別のラベルを取得
    const getSalaryTypeLabel = useCallback((typeId, salaryTypeOptions) => {
        return Object.keys(salaryTypeOptions)
            .find(key => salaryTypeOptions[key].toString() === typeId);
    }, []);

    // 給与単位のラベルを取得
    const getSalaryUnitLabel = useCallback((typeId, typeLabel, salaryUnitLabels, translations) => {
        if (!typeId || typeId === '0' || !typeLabel || !salaryUnitLabels[typeLabel]) {
            return '';
        }
        return translations[salaryUnitLabels[typeLabel]] || salaryUnitLabels[typeLabel];
    }, []);

    // 小数点以下の桁数を取得
    const getDecimalDigits = useCallback((min, max) => {
        const getDigits = (value) => {
            if (!value) return 0;
            const str = value.toString();
            return str.includes('.') ? str.split('.')[1].length : 0;
        };

        if (min && max) {
            return Math.max(getDigits(min), getDigits(max));
        }
        if (min) return getDigits(min);
        if (max) return getDigits(max);
        return 0;
    }, []);

    // 金額をフォーマット
    const formatAmount = useCallback((value, digits = 0) => {
        if (value == null) return '';
        return numberFormat(Number(value), digits);
    }, []);

    // 表示パターンを判定してラベルを生成
    const generateSalaryDisplayLabel = useCallback(({
        typeId, min, max, salaryTypeOptions, salaryUnitLabels,
        salaryStatistics, translations, salaryCurrency, upTo, from, label
    }) => {
        const typeLabel = getSalaryTypeLabel(typeId, salaryTypeOptions);
        const unitLabel = getSalaryUnitLabel(typeId, typeLabel, salaryUnitLabels, translations);

        const maxValue = typeId !== '0' ? salaryStatistics?.salary_type_ids?.[typeId]?.max ?? null : null;
        const minNum = min ? parseFloat(min) : 0;
        const maxNum = max ? parseFloat(max) : maxValue;

        // パターン1: 種別のみ選択
        if (!min && !max || (minNum === 0 && (maxValue !== null && maxNum === maxValue))) {
            // All選択時
            if (typeId === '0') {
                return label;
            }
            // 特定種別選択時
            if (typeLabel) {
                return typeLabel;
            }
            // フォールバック
            return label;
        }

        // 金額表示がある場合のみ、小数点以下の桁数を計算
        const digits = getDecimalDigits(min, max);

        // パターン2: 最大値のみ設定（〜まで）
        if ((minNum === 0 || min === '' || min == null) && max && maxValue !== null && maxNum < maxValue) {
            return `${upTo} ${salaryCurrency}${formatAmount(max, digits)}${unitLabel ? ' ' + unitLabel : ''}`;
        }

        // パターン3: 最小値のみ設定（〜から）
        if (min && minNum > 0 && (max === '' || max == null || (maxValue !== null && maxNum === maxValue))) {
            return `${from} ${salaryCurrency}${formatAmount(min, digits)}${unitLabel ? ' ' + unitLabel : ''}`;
        }

        // パターン4: 範囲設定（最小値〜最大値）
        if (min && minNum > 0 && max && maxValue !== null && maxNum < maxValue) {
            return `${salaryCurrency}${formatAmount(min, digits)} - ${salaryCurrency}${formatAmount(max, digits)}${unitLabel ? ' ' + unitLabel : ''}`;
        }

        // デフォルト
        return label;
    }, [getSalaryTypeLabel, getSalaryUnitLabel, getDecimalDigits, formatAmount]);

    return {
        generateSalaryDisplayLabel,
        getSalaryTypeLabel,
        getSalaryUnitLabel,
        getDecimalDigits,
        formatAmount
    };
};

export default useSalaryDisplayLabel; 